import { Trans } from 'react-i18next';
import { t } from 'i18next';
import { ChatFill, MegaphoneFill } from '@peacecover/icon';
import type { components } from '@pocapan/shared/types/schema';

type PostPreview = components['schemas']['PostPreview'];

interface ChatHeaderProps {
  isOther: boolean;
  post: PostPreview;
  onFollowUser?: () => void;
}

const ellipsisNickname = (nickname?: string) => {
  if (!nickname) return '';
  if (nickname.length > 4) {
    return `${nickname.slice(0, 4)}...`;
  }
  return nickname;
};

const ChatHeader = ({ isOther, post, onFollowUser }: ChatHeaderProps) => {
  const type = post.postType;
  if (type === 'comment') {
    return <CommentHeader parentContent={post.parent?.content ?? undefined} />;
  }

  if (type === 'megaphone' || type === 'quotation') {
    const megaphoneAuthorNickname = post.megaphonePost?.authorName || post.quotedPost?.authorName || '';
    return (
      <MegaphoneHeader
        isOther={isOther}
        nickname={post.authorName ?? undefined}
        megaphoneAuthorNickname={megaphoneAuthorNickname}
      />
    );
  }

  if (isOther) {
    return (
      <ChatPartnerHeader
        nickname={post.authorName ?? undefined}
        isAnonymous={post.anonymous}
        isFollowing={post.isFollowing}
        onFollowUser={onFollowUser}
      />
    );
  }

  return <MyChatHeader isAnonymous={post.anonymous} />;
};
export default ChatHeader;

const CommentHeader = ({ parentContent }: { parentContent?: string }) => {
  return (
    <div className="flex items-center gap-[4px]">
      <ChatFill size={12} className="text-text-main-lv4" />
      <p className="text-[11px] text-text-main-lv4 leading-[13px]">
        <Trans
          i18nKey="ChatUI.commentHeader"
          values={{ content: parentContent ? `"${ellipsisNickname(parentContent)}"` : '' }}
          components={{ span: <span className="max-w-[50px] truncate" /> }}
        />
      </p>
    </div>
  );
};

const MegaphoneHeader = ({
  isOther,
  nickname,
  megaphoneAuthorNickname
}: {
  isOther: boolean;
  nickname?: string;
  megaphoneAuthorNickname?: string;
}) => {
  return (
    <div className="flex items-center gap-[4px]">
      <MegaphoneFill size={12} className="text-text-main-lv4" />
      <p className="text-[11px] text-text-main-lv4 leading-[13px]">
        {isOther
          ? t('ChatUI.megaphone.other', {
              nickname: ellipsisNickname(nickname),
              authorNickname: ellipsisNickname(megaphoneAuthorNickname)
            })
          : t('ChatUI.megaphone.me', { authorNickname: ellipsisNickname(megaphoneAuthorNickname) })}
      </p>
    </div>
  );
};

const ChatPartnerHeader = ({
  nickname,
  isAnonymous,
  isFollowing,
  onFollowUser
}: {
  nickname?: string;
  isAnonymous?: boolean;
  isFollowing?: boolean;
  onFollowUser?: () => void;
}) => {
  return (
    <div className="flex items-start gap-[3px] ml-[6px]">
      <p className="text-[11px] text-text-main-lv4 leading-[13px] max-w-[123px] truncate">
        {nickname}
      </p>
      {!isAnonymous && !isFollowing && (
        <button
          className="inline-flex text-[10px] text-text-primary leading-[12px]"
          type="button"
          onClick={() => onFollowUser?.()}
        >
          {t('ChatUI.plusFollow')}
        </button>
      )}
    </div>
  );
};

const MyChatHeader = ({ isAnonymous }: { isAnonymous?: boolean }) => {
  return (
    <p className="text-[11px] text-text-main-lv4 leading-[13px] mr-[6px]">
      {isAnonymous && `${t('ChatUI.writeAnonymous')} • `} {t('ChatUI.me')}
    </p>
  );
};
